import { type FC, useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Box, Column, Columns, TextLink } from 'braid-design-system';
import { NumberParam, StringParam, useQueryParam } from 'use-query-params';
import {
  PAGE_QUERY_PARAM_KEY,
  PAGE_TAB_PARAM_KEY,
} from 'src/pages/CandidateManagementPage/queryParams';
import JobSection from 'src/components/app/JobSection';
import UnhandledErrorPageBlock from '../../components/app/UnhandledErrorPageBlock';
import * as pageBlockStyles from '../../components/layouts/PageBlock/PageBlock.css';
import { UserLoadState } from '../../stores/UserStore/types';
import type { IQuestionnaireStore } from '../../stores/JobStore/QuestionnaireStore';
import type { IJobStore } from '../../stores/JobStore';
import { getSignInUrl } from '../../pages/CandidateManagementPage/utils/getSignInUrl';
import PageAlert from './PageAlert';
import { useStores } from 'src/utils/hooks/useStores';
import CandidateList from 'src/components/list/index';
import { MobileFilterSection } from '../../components/app/MobileFilterSection';
import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import { IntercomConfiguration } from 'src/modules/intercom/IntercomConfiguration';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { isANZSite } from 'src/utils/site';
import { UserActionStatusProvider } from 'src/components/common/UserActionStatusContext';
import {
  getAnzCsPhoneNumberFromSite,
  getHelpCentreUrlFromSite,
} from '../../utils/getCsContactMethodMessageFromSite';
import { BillableApplicantsDialog } from '../CandidateManagementPage/BillableApplicantsDialog/BillableApplicantDialog';
import {
  trackCandidateCardStatus,
  trackClickBillableApplicantsDefinition,
  trackFreemiumButtonDisplay,
  trackFreemiumButtonPressedEvent,
  trackSelectAllProspectsClicked,
  trackSortOptionChangedEvent,
  trackUpdateStatusMultiple,
} from 'src/utils/events';
import { getDashisedBucket } from 'src/constants/jobApplicationStatus';
import { HOTJAR_TAG_SORT } from 'src/constants/hotjar';
import { sendHotjarTag } from 'src/utils/hotjar';
import { SUPPORT_BACK_BUTTON } from 'src/utils/buildHandleEnterAndSpaceKeyPress';
import { type PageTab, PageTabs } from '../CandidateManagementPage/PageTabs';
import loadable from 'sku/@loadable/component';
import { AppPageLayout } from './AppPageLayout';
import { useFilters } from 'src/components/app/FilterSideBar/useFilters';
import { MobileStickyHeader } from './MobileStickyHeader';
import { FreemiumButton } from './FreemiumButton';
import {
  useApplicationsFacetsQuery,
  useJobDetailsQuery,
} from './AppPageBlock.hirer.generated';
import useQueryParamsStore from 'src/utils/hooks/useQueryParamsStore';
import ActionToolbar from 'src/components/list/ActionToolbar';
import { FilterTabletDesktopContentLayout } from './FilterTabletDesktopContentLayout';
import { FilterDrawerTablet } from './FilterDrawerTablet';
import { FilterDesktop } from './FilterDesktop';
import AppErrorBoundary from 'src/components/app/AppErrorBoundary';
import type {
  ApplicationStatusFolder,
  QuestionnaireFilter,
} from './graphql/hirer-types.generated';
import useHandleStatusChange from '../hooks/useHandleStatusChange';
import { FilterScreeningQuestion } from './FilterScreeningQuestion';
import { FilterDrawerMobile } from './FilterDrawerMobile';
import { sleep } from 'src/utils/sleep';
import { LocationFilter } from './LocationFilter';
import { createPageAnalytics } from './createPageAnalytics';
import { getTrackJs } from 'src/utils/window';
import type { BUCKETS } from 'src/stores/JobApplicationStore/types';

const HirerJobActivity = loadable(
  () => import('src/components/app/HirerActivity/HirerJobActivity'),
);

const isPageTab = (value: unknown): value is PageTab =>
  value === 'applications' || value === 'notes';

interface AppPageBlockProps {
  employerHeader: JSX.Element;
}

export const CandidateManagementApp: FC<AppPageBlockProps> = observer(
  ({ employerHeader }) => {
    const { t } = useTranslations(translations);
    const { userStore, jobStore, isError } = useStores();
    const { site, language } = useAppConfig();

    if (userStore.userLoadState === UserLoadState.UNAUTHENTICATED) {
      return (
        <PageBlock>
          <PageAlert>
            {t('Sign in to see this page.', {
              Link: (children) => (
                <TextLink
                  key={UserLoadState.UNAUTHENTICATED}
                  href={getSignInUrl()}
                >
                  {children}
                </TextLink>
              ),
            })}
          </PageAlert>
        </PageBlock>
      );
    }

    if (userStore.userLoadState === UserLoadState.FORBIDDEN) {
      return (
        <PageBlock>
          <PageAlert>
            {t(
              "This account can't access this page. Would you like to switch accounts?",
              {
                Link: (children) => (
                  <TextLink key={UserLoadState.FORBIDDEN} href={getSignInUrl()}>
                    {children}
                  </TextLink>
                ),
              },
            )}
          </PageAlert>
        </PageBlock>
      );
    }

    if (!jobStore.hasAccess) {
      const csPhoneNumber = getAnzCsPhoneNumberFromSite(site);

      const noAccessToJobMessage = isANZSite(site)
        ? t(
            'If you think you should be one of them, ask your system administrator...',
            {
              TelLink: () => (
                <TextLink href={`tel:${csPhoneNumber}`}>
                  {csPhoneNumber}
                </TextLink>
              ),
            },
          )
        : t(
            'If you think you should be one of them, ask your system administrator - with Customer Service link',
            {
              Link: (children) => (
                <TextLink href={getHelpCentreUrlFromSite(site, language)}>
                  {children}
                </TextLink>
              ),
            },
          );
      return (
        <PageBlock>
          <PageAlert>
            {t('Only some people can see this page')}
            <br />
            {noAccessToJobMessage}
          </PageAlert>
        </PageBlock>
      );
    }

    if (isError || userStore.userLoadState === UserLoadState.ERROR) {
      return (
        <PageBlock>
          <Box marginY="small" marginX="gutter">
            <UnhandledErrorPageBlock />
          </Box>
        </PageBlock>
      );
    }

    return <AppPageBlock employerHeader={employerHeader} />;
  },
);

const AppPageBlock: FC<AppPageBlockProps> = observer(({ employerHeader }) => {
  const store = useStores();
  const {
    seekAdvertiserId,
    jobApplicationStore,
    userStore,
    isMobile,
    uiStore,
  } = store;
  const jobStore: IJobStore = store.jobStore;
  const questionStore: IQuestionnaireStore = jobStore.questionnaire;

  const { language, locale, site, router, environment } = useAppConfig();
  const [isFilterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Note: because of `currentPage`, only use pageAnalytics for CM Page's "candidate list"
  // Some events use "candidate details" (Profile Drawer) which we will address later
  const [pageAnalytics] = useState(() =>
    createPageAnalytics({
      locale,
      site,
      environment,
      userId: userStore.user.userId,
      seekAdvertiserId,
      trackJs: getTrackJs(),
      extraBaseAttributes: {
        seekListingId: jobStore.job.jobInformation.jobId,
        adProductName:
          jobStore.job.jobInformation.productDisplayName || 'Unknown',
        currentPage: 'candidate list',
        testingId: 'loyalty-beta-experience',
      },
    }),
  );

  const questions = jobApplicationStore.filtersStore.questions;

  const { filters, filterFns: useFiltersFns } = useFilters({
    language,
  });

  const [isAtTopOfPage, setIsAtTopOfPage] = useState(true);
  const [, setPageQueryString] = useQueryParam(
    PAGE_QUERY_PARAM_KEY,
    NumberParam,
  );
  const [selectedPageTabUntyped, setPageTabString] = useQueryParam(
    PAGE_TAB_PARAM_KEY,
    StringParam,
  );
  const [queryParams, setQueryParams] = useQueryParamsStore();
  const [isStickyOnDesktop, setIsStickyOnDesktop] = useState(false);
  const [isBillableApplicantsDialogOpen, setIsBillableApplicantsDialogOpen] =
    useState<boolean>(false);

  useEffect(() => {
    store.jobApplicationStore.updateFromQuery({
      queryParams: {
        jobId: queryParams.jobId,
        positionId: queryParams.positionId,
        bucket: queryParams.bucket,
        page: queryParams.page,
        selectedProspectId: queryParams.selectedProspectId,
        search: queryParams.search,
        overlay: queryParams.overlay,
        referrer: queryParams.referrer,
        tab: queryParams.tab,
      },
      language,
      initialLoad: false,
      filtersEventCaptureData: {
        filtered_apps_in_bucket:
          filters.filtersEventCaptureData.filtered_apps_in_bucket,
        filters_applied_to_bucket:
          filters.filtersEventCaptureData.filters_applied_to_bucket,
        total_apps_in_bucket:
          filters.filtersEventCaptureData.total_apps_in_bucket,
      },
      filters: filters.searchFilters,
    });
  }, [
    queryParams.jobId,
    queryParams.positionId,
    queryParams.bucket,
    queryParams.page,
    queryParams.selectedProspectId,
    queryParams.search,
    queryParams.overlay,
    queryParams.referrer,
    queryParams.tab,
    store,
    language,
    filters.filtersEventCaptureData.filtered_apps_in_bucket,
    filters.filtersEventCaptureData.filters_applied_to_bucket,
    filters.filtersEventCaptureData.total_apps_in_bucket,
    filters.searchFilters,
  ]);

  const setSelectedPageTab = (tab: PageTab) => {
    switch (tab) {
      case 'notes':
        setPageTabString('notes');
        break;
      default:
        setPageTabString(undefined);
    }
  };
  const selectedPageTab = isPageTab(selectedPageTabUntyped)
    ? selectedPageTabUntyped
    : 'applications';

  const shouldShowRecommendedCandidates = jobStore.job.isActive;

  const openBillableAlertFromBanner = () => {
    trackClickBillableApplicantsDefinition({
      actionOrigin: 'Candidate Listing - Billable Alert',
    });
    setIsBillableApplicantsDialogOpen(true);
  };

  const upgradeJobLink = jobStore.job.jobInformation.jobId
    ? router.upgradeJob({ jobId: jobApplicationStore.jobId })
    : '';

  const getJobDetailsResult = useJobDetailsQuery({
    variables: {
      advertiserId: seekAdvertiserId,
      jobId: jobApplicationStore.jobId.toString(),
      language,
    },
  });

  const shouldShowFreemium =
    upgradeJobLink &&
    userStore.user.canCreateJobs &&
    getJobDetailsResult.data?.getJobDetails?.canBeUpgraded;

  const canShowLocationFilter =
    getJobDetailsResult.data?.getJobDetails
      ?.canShowHomeLocationFilterInCandidateManagement;

  const hidePageTabs =
    isMobile && (!shouldShowRecommendedCandidates || !isAtTopOfPage);

  const isMustHaveToggleSelected =
    jobApplicationStore.filtersStore.isMustHaveToggleSelected;

  const onSortByChange: (params: { sortBy: string }) => Promise<void> = async ({
    sortBy,
  }) => {
    const defaultPage = 1;

    trackSortOptionChangedEvent({ sortMode: sortBy });
    sendHotjarTag(HOTJAR_TAG_SORT);

    await jobApplicationStore.loadJobApplications({
      sortBy,
      page: defaultPage,
      language,
      filters: filters.searchFilters,
    });
    setPageQueryString(defaultPage, SUPPORT_BACK_BUTTON);
  };

  const jobPostingCountry = jobStore.job.jobInformation.jobCountry;

  const locationFacetsResult = useApplicationsFacetsQuery({
    variables: {
      applicationsFacetsInput: {
        jobId: jobApplicationStore.jobId,
        filters: {
          // We are doing this because the UI allows for searching across all status folders at the moment.
          statusFolders: jobApplicationStore.lastResultSearchText
            ? []
            : ([
                jobApplicationStore.bucket,
              ] as unknown as ApplicationStatusFolder[]),
          searchText: jobApplicationStore.lastResultSearchText,
          homeLocationIds: filters.selectedHomeLocations.map(({ id }) => id),
          questionnaireFilters: jobApplicationStore.filtersStore
            .selectedRrFilters as unknown as QuestionnaireFilter[],
        },
      },
      descriptionInput: {
        displayCountry: jobPostingCountry.toUpperCase(),
        language,
      },
      displayDescriptionInput: {
        language,
      },
    },
    fetchPolicy: 'no-cache',
  });

  const { handleStatusChange } = useHandleStatusChange({
    filters: filters.searchFilters,
    filtersEventCaptureData: filters.filtersEventCaptureData,
  });

  const locationFacetsDataToUse = locationFacetsResult.data
    ? locationFacetsResult.data
    : locationFacetsResult.previousData;

  const renderLocationFilter = ({ mobile }: { mobile: boolean }) => (
    <LocationFilter
      hideLabel={mobile}
      dropdownBoxProps={{
        zIndex: mobile ? undefined : 'dropdown',
        boxShadow: mobile ? undefined : 'medium',
        position: mobile ? 'relative' : 'absolute',
      }}
      locations={
        locationFacetsDataToUse?.applicationsFacets.__typename ===
        'ApplicationsFacetsResponseSuccess'
          ? locationFacetsDataToUse.applicationsFacets.result.homeLocationFacets
          : []
      }
      loading={
        locationFacetsResult.loading && !locationFacetsResult.previousData
      }
      jobPostingCountry={jobPostingCountry}
      selectedHomeLocations={filters.selectedHomeLocations}
      setSelectedHomeLocations={({ homeLocations, changes }) => {
        changes.forEach((change) => {
          pageAnalytics.trackLink({
            event: 'searched_location_pressed',
            attributes: {
              searchLocationId: homeLocations.map(({ id }) => id),
              locationIdSelected: change.item.id,
              filterSelection: change.type,
            },
          });
        });
        useFiltersFns.setSelectedHomeLocations(homeLocations);
        jobApplicationStore.loadJobApplications({
          filters: {
            rrGlobalFilters: jobApplicationStore.filtersStore.selectedRrFilters,
            homeLocationIds: homeLocations.map(({ id }) => id),
          },
          language,
        });
      }}
    />
  );

  const screeningQuestionFilters = (
    <FilterScreeningQuestion
      questionnaire={questionStore}
      isMustHaveToggleSelected={isMustHaveToggleSelected}
      questions={questions}
      onMustHaveFilterToggled={useFiltersFns.onMustHaveFilterToggled}
      onParentFilterSelected={useFiltersFns.onParentFilterSelected}
      onFilterSelected={useFiltersFns.onFilterSelected}
    />
  );

  const shouldShowClearFilterButton = filters.selectedFilterCount > 0;
  const onClickStatusFolder = (bucket: BUCKETS) => {
    if (jobApplicationStore.bucket !== bucket) {
      jobApplicationStore.clearAllSelected();
    }
    setQueryParams.navigateToBucket(bucket);
  };
  const filterTabletDesktopContentLayout = (
    <FilterTabletDesktopContentLayout
      locationFilter={
        canShowLocationFilter ? renderLocationFilter({ mobile: false }) : null
      }
      screeningQuestionFilters={screeningQuestionFilters}
      onClickStatusFolder={onClickStatusFolder}
    />
  );

  const homeLocationIdsForTracking =
    filters.searchFilters.homeLocationIds || [];

  const renderPageContent = () => {
    if (selectedPageTab === 'applications') {
      return (
        <AppErrorBoundary>
          <Box
            data-cy="main-content"
            marginX={['none', 'xxsmall']}
            background="surface"
          >
            <Columns space={['none', 'none', 'small']}>
              <Column width="content">
                <FilterDesktop
                  shouldShowClearFilterButton={shouldShowClearFilterButton}
                  content={filterTabletDesktopContentLayout}
                  isStickyOnDesktop={isStickyOnDesktop}
                  resetFilters={useFiltersFns.resetFilters}
                />
              </Column>

              <Column>
                <CandidateList
                  actionToolbar={
                    <ActionToolbar
                      jobApplicationStore={jobApplicationStore}
                      updateStatusForSelectedApplications={(newBucket) => {
                        trackUpdateStatusMultiple({
                          objectInteraction:
                            jobApplicationStore.selectAllStatus,
                          bulkActionCount:
                            jobApplicationStore
                              .selectedJobApplicationUpToBulkMoveLimit.length,
                          applicationStatus: getDashisedBucket(newBucket), // new bucket/status
                          bucket: getDashisedBucket(jobApplicationStore.bucket), // old bucket/status
                          candidateCardStatus:
                            jobApplicationStore.rootStore.getCandidateCardStateForTracking(),
                          searchLocationId: homeLocationIdsForTracking,
                        });

                        return jobApplicationStore.updateStatusForSelectedApplications(
                          {
                            bucket: newBucket,
                            store: jobApplicationStore.rootStore,
                            reloadJobApplications: async () => {
                              await jobApplicationStore.loadJobApplications({
                                language,
                                filters: filters.searchFilters,
                              });
                            },
                            filtersEventCaptureData:
                              filters.filtersEventCaptureData,
                          },
                        );
                      }}
                      sortBy={jobApplicationStore.sortBy}
                      hasSearchText={jobApplicationStore.hasSearchText}
                      hasQuestion={Boolean(jobStore.questionnaire?.hasQuestion)}
                      onSortByChange={onSortByChange}
                      isMobile={isMobile}
                      isJobTabsSticky={uiStore.isJobTabsSticky}
                      showOldJobIntercom={jobStore.job.showOldJobIntercom}
                      selectAllStatus={jobApplicationStore.selectAllStatus}
                      selectAll={(isCheckboxChecked) => {
                        if (isCheckboxChecked) {
                          jobApplicationStore.clickClearAllSelected({
                            tracking: {
                              actionOrigin:
                                'Candidate Listing - Bulk Select Check Box',
                            },
                          });
                        } else {
                          jobApplicationStore.clickSelectAllOnCurrentPage();
                        }
                      }}
                      onExpandApplicationCardState={() => {
                        trackCandidateCardStatus({
                          candidateCardStatus: 'Expand',
                          actionOrigin: 'Candidate Listing - Listing Change',
                        });
                      }}
                      onCollapseApplicationCardState={() => {
                        trackCandidateCardStatus({
                          candidateCardStatus: 'Collapse',
                          actionOrigin: 'Candidate Listing - Listing Change',
                        });
                      }}
                      selectedFilterCount={filters.selectedFilterCount}
                      openFilterDrawer={() => setFilterDrawerOpen(true)}
                      addCandidate={async (candidateFormValues) => {
                        await jobApplicationStore.addCandidate(
                          candidateFormValues,
                          language,
                          async () => {
                            jobApplicationStore.loadJobApplications({
                              language,
                              filters: filters.searchFilters,
                            });
                          },
                        );
                      }}
                      selectAllJobApplicationsInBucket={({ onCompleted }) => {
                        jobApplicationStore.loadAllJobApplications({
                          selectAll: true,
                          onCompleted: () => {
                            trackSelectAllProspectsClicked({
                              objectInteraction:
                                jobApplicationStore.selectAllStatus,
                              bulkActionCount:
                                jobApplicationStore.selectedCount,
                            });
                            onCompleted();
                          },
                          language,
                          filters: filters.searchFilters,
                        });
                      }}
                    />
                  }
                  reloadJobApplicationsWithLastResultSearchText={() => {
                    jobApplicationStore.loadJobApplications({
                      searchText: jobApplicationStore.lastResultSearchText,
                      language,
                      filters: filters.searchFilters,
                    });
                  }}
                  clearTextAndReloadJobApplications={() => {
                    jobApplicationStore.updateSearchText('');
                    // FIXME: This is not updating the query param for search term, which is a bug
                    jobApplicationStore.loadJobApplications({
                      language,
                      searchText: jobApplicationStore.searchText,
                      filters: filters.searchFilters,
                    });
                  }}
                  openBillableDialog={openBillableAlertFromBanner}
                  useFiltersFns={useFiltersFns}
                  isMustHaveToggleSelected={isMustHaveToggleSelected}
                  filtersEventCaptureData={filters.filtersEventCaptureData}
                  handleStatusChange={handleStatusChange}
                  refetchFacets={async () => {
                    // NOTE: Status update is async so we need to wait a bit after the mutation has returned to refetch or else we'd get the same results.
                    // This also solves the issue where
                    // 1. AppPageBlock is updated by MobX's `observer` function
                    // 2. Route is updated by changing of search params (inside onUpdateSuccess)
                    //
                    // (1.) runs first, and when it does, it still has the old query params with `&selected=<id>` which causes
                    // the profile drawer to re-open when `store.jobApplicationStore.updateFromQuery` is called in AppPageBlock
                    // Because of this, the drawer re-opens after it has been closed
                    // Reported: https://seekchat.slack.com/archives/C24PK4XV3/p1719356837551939
                    await sleep(1500);
                    await locationFacetsResult.refetch();
                  }}
                  homeLocationIdsForTracking={homeLocationIdsForTracking}
                />
              </Column>
            </Columns>
          </Box>
        </AppErrorBoundary>
      );
    }
    if (selectedPageTab === 'notes') {
      return <HirerJobActivity handleStatusChange={handleStatusChange} />;
    }
  };

  const pageTabs = (
    <PageTabs
      isStickyOnDesktop={isStickyOnDesktop}
      selectedPageTab={selectedPageTab}
      setIsStickyOnDesktop={setIsStickyOnDesktop}
      setSelectedPageTab={setSelectedPageTab}
      showRecommendedCandidates={shouldShowRecommendedCandidates}
      jobId={jobApplicationStore.jobId}
      onApplicationsTabClick={() =>
        jobApplicationStore.loadJobApplications({
          language,
          filters: filters.searchFilters,
        })
      }
    />
  );

  const mobileFilterSection = (
    <MobileFilterSection
      sortBy={jobApplicationStore.sortBy}
      hasSearchText={jobApplicationStore.hasSearchText}
      hasQuestion={Boolean(jobStore.questionnaire?.hasQuestion)}
      onSortByChange={onSortByChange}
      selectedFilterCount={filters.selectedFilterCount}
      openFilterDrawer={() => setFilterDrawerOpen(true)}
    />
  );

  return (
    <UserActionStatusProvider
      jobId={`${jobApplicationStore.jobId}`}
      userId={store.userStore.user.userId || 0}
    >
      <IntercomConfiguration />
      <AppPageLayout
        mobileStickyHeader={
          isMobile && (
            <MobileStickyHeader
              isAtTopOfPage={isAtTopOfPage}
              setIsAtTopOfPage={setIsAtTopOfPage}
              employerHeader={employerHeader}
              pageTabs={shouldShowRecommendedCandidates && pageTabs}
              mobileFilterSection={mobileFilterSection}
            />
          )
        }
        jobSection={{
          boxShadow: hidePageTabs ? 'small' : undefined,
          element: (
            <JobSection
              job={jobStore.job}
              isRecommendedCandidatesTabShown={shouldShowRecommendedCandidates}
              openBillableDialog={() => {
                trackClickBillableApplicantsDefinition({
                  actionOrigin:
                    'Candidate Listing - Billable Applicants Button',
                });
                setIsBillableApplicantsDialogOpen(true);
              }}
              freemiumButton={
                shouldShowFreemium ? (
                  <FreemiumButton
                    link={upgradeJobLink}
                    onFreemiumButtonClicked={() =>
                      trackFreemiumButtonPressedEvent()
                    }
                    onFreemiumButtonLoaded={() => {
                      trackFreemiumButtonDisplay({
                        hasJobUpgradeButton: true,
                      });
                    }}
                  />
                ) : null
              }
            />
          ),
        }}
        pageTabs={!hidePageTabs && pageTabs}
        mobileFilterSection={mobileFilterSection}
        content={renderPageContent()}
        billableApplicantsDialog={
          jobStore.job.jobInformation.billableApplicants && (
            <BillableApplicantsDialog
              hasBillableApplicantsCountError={
                !jobStore.job.jobInformation.billableApplicants.success
              }
              isOpen={isBillableApplicantsDialogOpen}
              mandatoryQuestions={questionStore.getMandatoryQuestions()}
              onClose={() => {
                setIsBillableApplicantsDialogOpen(false);
              }}
            />
          )
        }
      />
      {isMobile ? (
        <FilterDrawerMobile
          showDrawer={isFilterDrawerOpen}
          setShowDrawer={setFilterDrawerOpen}
          statusFilters={{
            statusFolders: jobApplicationStore.bucketStatsStore.bucketInfo.map(
              (bucketInfo) => ({
                ...bucketInfo,
                selected:
                  bucketInfo.type === jobApplicationStore.bucket &&
                  !jobApplicationStore.lastResultSearchText,
              }),
            ),
            onClickStatusFolder,
          }}
          screeningQuestionFilters={
            questionStore.hasQuestion
              ? {
                  content: screeningQuestionFilters,
                  selectedQuestions:
                    jobApplicationStore.filtersStore.selectedQuestionAnswers.map(
                      ({ question }) => question.statement ?? question.text,
                    ),
                }
              : null
          }
          shouldShowClearFilterButton={shouldShowClearFilterButton}
          useFiltersFns={useFiltersFns}
          locationFilter={
            canShowLocationFilter
              ? {
                  content: renderLocationFilter({
                    mobile: true,
                  }),
                  selectedLocations: filters.selectedHomeLocations.map(
                    ({ name }) => name,
                  ),
                }
              : null
          }
        />
      ) : (
        <FilterDrawerTablet
          isOpen={isFilterDrawerOpen}
          close={() => setFilterDrawerOpen(false)}
          content={filterTabletDesktopContentLayout}
          resetFilters={useFiltersFns.resetFilters}
          shouldShowClearFilterButton={shouldShowClearFilterButton}
        />
      )}
    </UserActionStatusProvider>
  );
});

const PageBlock: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <Box
    className={pageBlockStyles.pageBlock}
    display="flex"
    flexDirection="column"
    paddingY="none"
    paddingX={['none', 'xsmall']}
    marginY="none"
  >
    {children}
  </Box>
);
